import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import config from '../config';
import { ZoomHint, ImageLightbox } from './ZoomableImage';
import {
    getIdToken,
    fetchBatchEventStatus,
    toggleAttended,
    resolveEventStatus,
} from '../attendedUtils';

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatPostedAt(ts) {
    if (!ts) return '';
    const d = new Date(ts);
    return d.toLocaleDateString('en-US', {
        month: 'long', day: 'numeric', year: 'numeric',
    }) + ',  ' + d.toLocaleTimeString('en-US', {
        hour: 'numeric', minute: '2-digit', hour12: true,
    });
}

function formatEventDate(dateStr) {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
        weekday: 'long', month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC',
    });
}

function formatDiscussionTs(ts) {
    if (!ts) return '';
    return new Date(ts).toLocaleDateString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
    });
}

async function copyToClipboard(text) {
    try {
        await navigator.clipboard.writeText(text);
        return true;
    } catch {
        try {
            const el = document.createElement('textarea');
            el.value = text;
            el.style.position = 'fixed';
            el.style.opacity = '0';
            document.body.appendChild(el);
            el.select();
            document.execCommand('copy');
            document.body.removeChild(el);
            return true;
        } catch {
            return false;
        }
    }
}

// ── Inline discussion reply (full edit/delete parity with EventDetail) ──────

function InlineReply({ reply, currentUserId, isAdmin, onEdit, onDelete }) {
    const [editing, setEditing] = useState(false);
    const [editBody, setEditBody] = useState(reply.body);
    const [saving, setSaving] = useState(false);

    const isOwn = currentUserId && reply.author_id === currentUserId;
    const canDelete = isOwn || isAdmin;

    const handleSave = async () => {
        if (!editBody.trim()) return;
        setSaving(true);
        try {
            const token = await getIdToken();
            const res = await axios.put(`${config.API_URL}/events/replies/${reply.id}`, { body: editBody.trim() }, { headers: { Authorization: token } });
            onEdit(reply.id, res.data.body);
            setEditing(false);
        } catch (e) {
            alert('Failed to update reply.');
        } finally { setSaving(false); }
    };

    const handleDelete = async () => {
        if (!window.confirm('Delete this reply? This cannot be undone.')) return;
        try {
            const token = await getIdToken();
            await axios.put(`${config.API_URL}/events/replies/${reply.id}`, { is_deleted: true }, { headers: { Authorization: token } });
            onDelete(reply.id);
        } catch (e) { alert('Failed to delete reply.'); }
    };

    return (
        <div style={S.inlineReplyItem}>
            <div style={S.inlinePostMeta}>
                {reply.author_is_public && reply.author_username ? (
                    <Link to={`/u/${reply.author_username}`} style={S.inlineAuthorName}>{reply.author_display_name || 'Unknown'}</Link>
                ) : (
                    <span style={S.inlineAuthorName}>{reply.author_display_name || 'Unknown'}</span>
                )}
                <span style={S.inlinePostTime}>{formatDiscussionTs(reply.created_at)}</span>
                {reply.updated_at !== reply.created_at && <span style={S.inlineEditedTag}>(edited)</span>}
            </div>
            {editing ? (
                <div style={S.inlineComposer}>
                    <textarea value={editBody} onChange={e => setEditBody(e.target.value)} style={{ ...S.inlineTextarea, minHeight: '50px' }} />
                    <div style={S.inlineComposerActions}>
                        <button onClick={handleSave} disabled={saving} style={S.inlinePostBtn}>{saving ? 'Saving...' : 'Save'}</button>
                        <button onClick={() => { setEditing(false); setEditBody(reply.body); }} style={S.inlineCancelBtn}>Cancel</button>
                    </div>
                </div>
            ) : (
                <>
                    <div style={S.inlinePostBody}>{reply.body}</div>
                    {(isOwn || canDelete) && (
                        <div style={S.inlinePostActions}>
                            {isOwn && <button onClick={() => setEditing(true)} style={S.inlineActionBtn}>Edit</button>}
                            {canDelete && <button onClick={handleDelete} style={S.inlineActionBtn}>Delete</button>}
                        </div>
                    )}
                </>
            )}
        </div>
    );
}

// ── Inline discussion post (full reply/edit/delete parity) ──────────────────

function InlinePost({ post, currentUserId, isAdmin, onEditPost, onDeletePost, onAddReply, onEditReply, onDeleteReply }) {
    const [editing, setEditing] = useState(false);
    const [editBody, setEditBody] = useState(post.body);
    const [saving, setSaving] = useState(false);
    const [replyOpen, setReplyOpen] = useState(false);
    const [replyBody, setReplyBody] = useState('');
    const [replySaving, setReplySaving] = useState(false);

    const isOwn = currentUserId && post.author_id === currentUserId;
    const canDelete = isOwn || isAdmin;

    const handleSave = async () => {
        if (!editBody.trim()) return;
        setSaving(true);
        try {
            const token = await getIdToken();
            const res = await axios.put(`${config.API_URL}/events/posts/${post.id}`, { body: editBody.trim() }, { headers: { Authorization: token } });
            onEditPost(post.id, res.data.body);
            setEditing(false);
        } catch (e) { alert('Failed to update post.'); }
        finally { setSaving(false); }
    };

    const handleDelete = async () => {
        const warnReplies = post.replies && post.replies.length > 0
            ? ` This post has ${post.replies.length} ${post.replies.length === 1 ? 'reply' : 'replies'} that will be deleted along with it.`
            : '';
        if (!window.confirm(`Delete this post? This cannot be undone.${warnReplies}`)) return;
        try {
            const token = await getIdToken();
            await axios.put(`${config.API_URL}/events/posts/${post.id}`, { is_deleted: true }, { headers: { Authorization: token } });
            onDeletePost(post.id);
        } catch (e) { alert('Failed to delete post.'); }
    };

    const handleReply = async () => {
        if (!replyBody.trim()) return;
        setReplySaving(true);
        try {
            const token = await getIdToken();
            const res = await axios.post(`${config.API_URL}/events/${post.event_id}/posts/${post.id}/replies`, { body: replyBody.trim() }, { headers: { Authorization: token } });
            onAddReply(post.id, res.data);
            setReplyBody('');
            setReplyOpen(false);
        } catch (e) { alert('Failed to post reply.'); }
        finally { setReplySaving(false); }
    };

    return (
        <div style={S.inlinePostItem}>
            <div style={S.inlinePostMeta}>
                {post.author_is_public && post.author_username ? (
                    <Link to={`/u/${post.author_username}`} style={S.inlineAuthorName}>{post.author_display_name || 'Unknown'}</Link>
                ) : (
                    <span style={S.inlineAuthorName}>{post.author_display_name || 'Unknown'}</span>
                )}
                <span style={S.inlinePostTime}>{formatDiscussionTs(post.created_at)}</span>
                {post.updated_at !== post.created_at && <span style={S.inlineEditedTag}>(edited)</span>}
            </div>

            {editing ? (
                <div style={S.inlineComposer}>
                    <textarea value={editBody} onChange={e => setEditBody(e.target.value)} style={S.inlineTextarea} />
                    <div style={S.inlineComposerActions}>
                        <button onClick={handleSave} disabled={saving} style={S.inlinePostBtn}>{saving ? 'Saving...' : 'Save'}</button>
                        <button onClick={() => { setEditing(false); setEditBody(post.body); }} style={S.inlineCancelBtn}>Cancel</button>
                    </div>
                </div>
            ) : (
                <>
                    <div style={S.inlinePostBody}>{post.body}</div>
                    <div style={S.inlinePostActions}>
                        {currentUserId && (
                            <button onClick={() => setReplyOpen(r => !r)} style={S.inlineReplyBtn}>↩ Reply</button>
                        )}
                        {isOwn && (
                            <button onClick={() => setEditing(true)} style={S.inlineActionBtn}>Edit</button>
                        )}
                        {canDelete && (
                            <button onClick={handleDelete} style={S.inlineActionBtn}>Delete</button>
                        )}
                    </div>
                </>
            )}

            {/* Replies */}
            {post.replies && post.replies.length > 0 && (
                <div style={S.inlineRepliesWrap}>
                    {post.replies.map(r => (
                        <InlineReply
                            key={r.id}
                            reply={r}
                            currentUserId={currentUserId}
                            isAdmin={isAdmin}
                            onEdit={(rid, body) => onEditReply(post.id, rid, body)}
                            onDelete={(rid) => onDeleteReply(post.id, rid)}
                        />
                    ))}
                </div>
            )}

            {/* Reply composer */}
            {replyOpen && (
                <div style={{ ...S.inlineComposer, marginTop: '10px', marginLeft: '16px' }}>
                    <textarea
                        value={replyBody}
                        onChange={e => setReplyBody(e.target.value)}
                        placeholder="Write a reply..."
                        style={{ ...S.inlineTextarea, minHeight: '50px' }}
                    />
                    <div style={S.inlineComposerActions}>
                        <button onClick={handleReply} disabled={replySaving} style={S.inlinePostBtn}>{replySaving ? 'Posting...' : 'Reply'}</button>
                        <button onClick={() => { setReplyOpen(false); setReplyBody(''); }} style={S.inlineCancelBtn}>Cancel</button>
                    </div>
                </div>
            )}
        </div>
    );
}

// ── Inline footnote card (read-only, mirrors EventDetail's Footnotes tab) ───

function InlineFootnote({ footnote, index }) {
    return (
        <div style={S.footnoteCard}>
            <div style={S.footnoteNumber}>{index + 1}</div>
            <div style={S.footnoteContent}>
                <div style={S.footnoteDescription}>{footnote.description}</div>
                <div style={S.footnoteMeta}>
                    {footnote.artist_name && (
                        <Link
                            to={`/artists/${footnote.artist_slug || footnote.artist_id}`}
                            style={S.footnoteArtistLink}
                        >
                            {footnote.artist_name}
                        </Link>
                    )}
                    {footnote.url && (
                        <a href={footnote.url} target="_blank" rel="noopener noreferrer" style={S.footnoteUrlLink}>
                            Source ↗
                        </a>
                    )}
                </div>
            </div>
        </div>
    );
}

// ── Feed Card ─────────────────────────────────────────────────────────────────

// ── Zoomable card image ───────────────────────────────────────────────────────
// Hover shows the shared "+" ZoomHint; click opens the shared ImageLightbox
// (magnifier) with a "View Show →" link to the event — same pattern as the
// Flyers tabs (FlyerHistoryTabs.js) and EventDetail. Replaces the old
// click-through <Link> wrapper. A broken image hides the whole image column,
// as the old onError handler did.
function CardImage({ src, alt, caption, eventPath }) {
    const [hovered, setHovered] = useState(false);
    const [open, setOpen]       = useState(false);
    const [failed, setFailed]   = useState(false);

    if (!src || failed) return null;

    return (
        <div style={S.imageWrap}>
            <div
                style={{ ...S.imageBox, cursor: 'zoom-in' }}
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
                onClick={() => setOpen(true)}
                title="View larger"
            >
                <img
                    src={src}
                    alt={alt}
                    style={{ ...S.cardImage, opacity: hovered ? 0.85 : 1, transition: 'opacity 0.15s ease' }}
                    onError={() => setFailed(true)}
                />
                <ZoomHint visible={hovered} />
            </div>
            {open && (
                <ImageLightbox
                    src={src}
                    alt={alt}
                    caption={caption}
                    viewShowTo={eventPath}
                    onClose={() => setOpen(false)}
                />
            )}
        </div>
    );
}

function FeedCard({ item, status, onAttendedChange, currentUserId, isAdmin }) {
    const isCreate = item.feed_type === 'create';
    const label    = isCreate ? 'Event Created' : 'Event Updated';

    const eventPath    = `/events/${item.event_slug || item.event_id}`;
    const festivalPath = item.festival_slug ? `/festivals/${item.festival_slug}` : null;

    const { attended = false, performed = false, worked = false } = status || {};
    const eventStatus = resolveEventStatus(performed, worked, attended);
    const [attendedLoading, setAttendedLoading] = useState(false);

    // Lazily-loaded event detail (posts + footnotes), shared by both expand panels
    const [detail, setDetail]               = useState(null);
    const [detailLoading, setDetailLoading]  = useState(false);
    const [detailError, setDetailError]      = useState(null);

    const [discussionOpen, setDiscussionOpen] = useState(false);
    const [footnotesOpen,  setFootnotesOpen]  = useState(false);

    const [postCount, setPostCount] = useState(item.post_count || 0);
    const [newComment, setNewComment] = useState('');
    const [commentPosting, setCommentPosting] = useState(false);
    const [commentError, setCommentError] = useState(null);

    const [copied, setCopied] = useState(false);

    const ensureDetailLoaded = useCallback(async () => {
        if (detail || detailLoading) return;
        setDetailLoading(true);
        setDetailError(null);
        try {
            const res = await axios.get(`${config.API_URL}/events/${item.event_slug || item.event_id}`);
            setDetail({
                posts: (res.data.posts || []).map(p => ({ ...p, event_id: item.event_id })),
                footnotes: res.data.footnotes || [],
            });
        } catch (e) {
            setDetailError('Failed to load.');
        } finally {
            setDetailLoading(false);
        }
    }, [detail, detailLoading, item.event_slug, item.event_id]);

    const handleToggleDiscussion = () => {
        const next = !discussionOpen;
        setDiscussionOpen(next);
        if (next) ensureDetailLoaded();
    };

    const handleToggleFootnotes = () => {
        const next = !footnotesOpen;
        setFootnotesOpen(next);
        if (next) ensureDetailLoaded();
    };

    const handleToggleAttended = async () => {
        if (attendedLoading) return;
        setAttendedLoading(true);
        try {
            const result = await toggleAttended(item.event_id);
            onAttendedChange(item.event_id, result);
        } catch (e) {
            console.error('toggleAttended error:', e);
        } finally {
            setAttendedLoading(false);
        }
    };

    const handlePostComment = async () => {
        if (!newComment.trim() || commentPosting) return;
        setCommentPosting(true);
        setCommentError(null);
        try {
            const token = await getIdToken();
            const res = await axios.post(
                `${config.API_URL}/events/${item.event_id}/posts`,
                { body: newComment.trim() },
                { headers: { Authorization: token } }
            );
            setDetail(prev => ({
                posts: [...((prev && prev.posts) || []), { ...res.data, event_id: item.event_id }],
                footnotes: (prev && prev.footnotes) || [],
            }));
            setPostCount(c => c + 1);
            setNewComment('');
        } catch (e) {
            setCommentError('Failed to post comment.');
        } finally {
            setCommentPosting(false);
        }
    };

    // ── Discussion CRUD callbacks (mirror EventDetail.js state updates) ──────
    const onEditPost = (postId, body) => {
        setDetail(prev => ({ ...prev, posts: prev.posts.map(p => p.id === postId ? { ...p, body } : p) }));
    };
    const onDeletePost = (postId) => {
        setDetail(prev => ({ ...prev, posts: prev.posts.filter(p => p.id !== postId) }));
        setPostCount(c => Math.max(0, c - 1));
    };
    const onAddReply = (postId, reply) => {
        setDetail(prev => ({ ...prev, posts: prev.posts.map(p => p.id === postId ? { ...p, replies: [...(p.replies || []), reply] } : p) }));
    };
    const onEditReply = (postId, replyId, body) => {
        setDetail(prev => ({ ...prev, posts: prev.posts.map(p => p.id === postId ? { ...p, replies: p.replies.map(r => r.id === replyId ? { ...r, body } : r) } : p) }));
    };
    const onDeleteReply = (postId, replyId) => {
        setDetail(prev => ({ ...prev, posts: prev.posts.map(p => p.id === postId ? { ...p, replies: p.replies.filter(r => r.id !== replyId) } : p) }));
    };

    const handleCopyLink = async () => {
        const url = `${window.location.origin}${eventPath}`;
        const ok = await copyToClipboard(url);
        if (ok) {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        }
    };

    return (
        <div style={S.card}>
            {/* Top row: text left, image right */}
            <div style={S.cardInner}>

                {/* Left: text content */}
                <div style={S.cardText}>
                    {/* Header row */}
                    <div style={S.cardHeader}>
                        <span style={S.feedLabel}>{label}</span>
                        <span style={S.headerSep}> – Posted by </span>
                        <span style={S.postedBy}>{item.posted_by || 'SHARC User'}</span>
                        <span style={S.postedAt}>{formatPostedAt(item.ts)}</span>
                    </div>

                    {/* Event date + venue + location */}
                    <div style={S.eventDate}>{formatEventDate(item.event_date)}</div>
                    <div style={S.metaLine}>
                        {item.venue_slug ? <Link to={`/venues/${item.venue_slug}`} style={S.venueLink}>{item.venue_name}</Link> : <span style={S.venueName}>{item.venue_name}</span>}
                        {(item.venue_city || item.state_name || item.country_name) && (
                            <span style={S.location}>
                                {' '}in {[item.venue_city, item.state_name, item.country_name].filter(Boolean).join(', ')}
                            </span>
                        )}
                    </div>

                    {/* Title (if present) */}
                    {item.event_title && (
                        <div style={S.eventTitle}>
                            <Link to={eventPath} style={S.purpleLink}>{item.event_title}</Link>
                        </div>
                    )}

                    {/* Festival */}
                    {festivalPath && (
                        <div style={S.metaLine}>
                            <strong>
                                <Link to={festivalPath} style={S.purpleLink}>{item.festival_name}</Link>
                            </strong>
                        </div>
                    )}

                    {/* Lineup — JSON array of {name, slug} objects */}
                    {item.lineup && item.lineup.length > 0 && (
                        <div style={S.lineup}>
                            {item.lineup.map((artist, idx) => (
                                <span key={artist.slug || idx}>
                                    {idx > 0 && <span style={S.lineupSep}>, </span>}
                                    {artist.slug
                                        ? <Link to={`/artists/${artist.slug}`} style={S.artistLink}>{artist.name}</Link>
                                        : <strong style={{ color: '#fff' }}>{artist.name}</strong>
                                    }
                                </span>
                            ))}
                        </div>
                    )}

                    {/* View event link */}
                    <div style={{ marginTop: '10px' }}>
                        <Link to={eventPath} style={S.viewLink}>View Event →</Link>
                    </div>
                </div>

                {/* Right: image (hover "+", click to enlarge) */}
                <CardImage
                    src={item.image_url}
                    alt={item.event_title || item.headliner_name || 'Event'}
                    caption={[formatEventDate(item.event_date), item.venue_name].filter(Boolean).join(' · ')}
                    eventPath={eventPath}
                />
            </div>

            {/* Action row — full card width, not constrained by the image column */}
            <div style={S.actionRow}>
                {eventStatus === 'performed' ? (
                    <span style={S.performedBadge}>Performed</span>
                ) : eventStatus === 'worked' ? (
                    <span style={S.workedBadge}>Worked</span>
                ) : (
                    <button
                        onClick={handleToggleAttended}
                        disabled={attendedLoading}
                        style={attended ? S.attendedBtnOn : S.attendedBtnOff}
                    >
                        {attendedLoading ? '…' : attended ? '✓ Attended' : 'Attended?'}
                    </button>
                )}

                <button style={S.actionLinkBtn} onClick={handleToggleDiscussion}>
                    Discussion ({postCount}) <span style={S.chevron}>{discussionOpen ? '▲' : '▼'}</span>
                </button>

                <button style={S.actionLinkBtn} onClick={handleToggleFootnotes}>
                    Footnotes ({item.footnote_count || 0}) <span style={S.chevron}>{footnotesOpen ? '▲' : '▼'}</span>
                </button>

                <button style={S.actionLinkBtn} onClick={handleCopyLink}>
                    Copy Link
                </button>

                {copied && <span style={S.copiedToast}>Link Copied to Clipboard</span>}
            </div>

            {/* ── Inline Discussion panel — full card width ── */}
            {discussionOpen && (
                <div style={S.inlinePanel}>
                    {detailLoading && !detail && (
                        <div style={S.inlineLoading}>Loading discussion...</div>
                    )}
                    {detailError && !detail && (
                        <div style={S.inlineError}>{detailError}</div>
                    )}
                    {detail && (
                        <>
                            {detail.posts.length === 0 ? (
                                <div style={S.inlineEmpty}>No posts yet. Start the discussion!</div>
                            ) : (
                                <div style={S.inlinePostsList}>
                                    {detail.posts.map(post => (
                                        <InlinePost
                                            key={post.id}
                                            post={post}
                                            currentUserId={currentUserId}
                                            isAdmin={isAdmin}
                                            onEditPost={onEditPost}
                                            onDeletePost={onDeletePost}
                                            onAddReply={onAddReply}
                                            onEditReply={onEditReply}
                                            onDeleteReply={onDeleteReply}
                                        />
                                    ))}
                                </div>
                            )}
                            {currentUserId ? (
                                <div style={S.inlineComposer}>
                                    <textarea
                                        value={newComment}
                                        onChange={e => setNewComment(e.target.value)}
                                        placeholder="Add a comment..."
                                        style={S.inlineTextarea}
                                    />
                                    {commentError && <div style={S.inlineError}>{commentError}</div>}
                                    <div style={S.inlineComposerActions}>
                                        <button
                                            onClick={handlePostComment}
                                            disabled={commentPosting || !newComment.trim()}
                                            style={S.inlinePostBtn}
                                        >
                                            {commentPosting ? 'Posting...' : 'Post'}
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <div style={S.inlineSignInPrompt}>Sign in to join the discussion.</div>
                            )}
                        </>
                    )}
                </div>
            )}

            {/* ── Inline Footnotes panel — full card width ── */}
            {footnotesOpen && (
                <div style={S.inlinePanel}>
                    {detailLoading && !detail && (
                        <div style={S.inlineLoading}>Loading footnotes...</div>
                    )}
                    {detailError && !detail && (
                        <div style={S.inlineError}>{detailError}</div>
                    )}
                    {detail && (
                        detail.footnotes.length === 0 ? (
                            <div style={S.inlineEmpty}>No footnotes for this event.</div>
                        ) : (
                            <div style={S.footnoteList}>
                                {detail.footnotes.map((fn, idx) => (
                                    <InlineFootnote key={fn.id} footnote={fn} index={idx} />
                                ))}
                            </div>
                        )
                    )}
                </div>
            )}
        </div>
    );
}

// Normalize for search: strip diacritics and punctuation, same approach
// used by the entity pickers elsewhere on the site (e.g. AddEvent.js) so
// "motorhead" still matches "Motörhead".
const normalizeStr = (str) =>
    (str || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/['\u2018\u2019\u201b`\-.,:!?&]/g, '')
        .toLowerCase();

// ── Entity Picker (generic multi-select with chip + search, no create) ──────
// Mirrors the PromoterPicker pattern used when assigning promoters to an
// event (AddEvent.js), minus the "create new" affordance — a feed filter
// should never create a record.
function EntityPicker({ items, selectedIds, onChange, placeholder, renderLabel, renderChip }) {
    const [search, setSearch] = useState('');
    const q = normalizeStr(search);
    const filtered = search
        ? items.filter(it => normalizeStr(renderLabel(it)).includes(q))
        : items;

    const toggle = (id) => {
        if (selectedIds.includes(id)) onChange(selectedIds.filter(i => i !== id));
        else onChange([...selectedIds, id]);
    };

    const selectedItems = items.filter(it => selectedIds.includes(it.id));
    const showDropdown = search.length > 0;

    return (
        <div>
            {selectedItems.length > 0 && (
                <div style={S.pickerChips}>
                    {selectedItems.map(it => (
                        <div key={it.id} style={S.pickerChip}>
                            {renderChip ? renderChip(it) : renderLabel(it)}
                            <span onClick={() => toggle(it.id)} style={S.pickerChipRemove}>✕</span>
                        </div>
                    ))}
                </div>
            )}
            <div style={{ position: 'relative' }}>
                <input
                    type="text"
                    placeholder={placeholder}
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    style={{ ...S.pickerInput, borderRadius: showDropdown ? '4px 4px 0 0' : '4px' }}
                    autoComplete="off"
                />
                {showDropdown && (
                    <div style={S.pickerDropdown}>
                        {filtered.length === 0 ? (
                            <div style={S.pickerNoResults}>No results found</div>
                        ) : (
                            filtered.slice(0, 50).map(it => (
                                <div
                                    key={it.id}
                                    onMouseDown={() => { toggle(it.id); setSearch(''); }}
                                    style={{
                                        ...S.pickerOption,
                                        color: selectedIds.includes(it.id) ? '#bb86fc' : '#e0e0e0',
                                        backgroundColor: selectedIds.includes(it.id) ? '#1e1e1e' : 'transparent',
                                    }}
                                >
                                    {renderLabel(it)}
                                    {selectedIds.includes(it.id) && <span style={{ marginLeft: '8px' }}>✓</span>}
                                </div>
                            ))
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

// ── Feed View editor panel (Create New / Edit) ───────────────────────────────
function FeedViewEditor({
    mode, form, setForm, onSave, onCancel, saving, error,
    pickerData, pickerLoading, countries, editorStates,
}) {
    const set = (field) => (val) => setForm(prev => ({ ...prev, [field]: val }));

    const handleCountryChange = (e) => {
        setForm(prev => ({ ...prev, country_id: e.target.value, state_id: '' }));
    };

    return (
        <div style={S.editorPanel}>
            <div style={S.editorHeader}>{mode === 'create' ? 'Create Feed View' : 'Edit Feed View'}</div>

            <div style={S.editorField}>
                <label style={S.editorLabel}>Name</label>
                <input
                    type="text"
                    value={form.name}
                    onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g. West Coast Punk Shows"
                    style={S.editorInput}
                />
            </div>

            <div style={S.editorField}>
                <label style={S.editorLabel}>Sort By</label>
                <select
                    style={S.editorSelect}
                    value={form.sort_by}
                    onChange={e => set('sort_by')(e.target.value)}
                >
                    <option value="posted_desc">Posted Date (Most Recent to Oldest)</option>
                    <option value="posted_asc">Posted Date (Oldest to Most Recent)</option>
                    <option value="event_desc">Event Date (Most Recent to Oldest)</option>
                    <option value="event_asc">Event Date (Oldest to Most Recent)</option>
                </select>
            </div>

            <div style={S.editorField}>
                <label style={S.editorLabel}>Event Create/Change Visibility</label>
                <select
                    style={S.editorSelect}
                    value={form.event_visibility}
                    onChange={e => set('event_visibility')(e.target.value)}
                >
                    <option value="all">Show All Updates</option>
                    <option value="latest">Show Only The Most Recent Update</option>
                </select>
            </div>

            {pickerLoading && !pickerData && (
                <div style={S.editorLoading}>Loading filter options...</div>
            )}

            {pickerData && (
                <>
                    <div style={S.editorField}>
                        <label style={S.editorLabel}>Artist</label>
                        <EntityPicker
                            items={pickerData.artists}
                            selectedIds={form.artist_ids}
                            onChange={set('artist_ids')}
                            placeholder="Search artists..."
                            renderLabel={a => `${a.name}${a.city ? ` — ${a.city}` : ''}`}
                            renderChip={a => a.name}
                        />
                    </div>

                    <div style={S.editorField}>
                        <label style={S.editorLabel}>Festival</label>
                        <EntityPicker
                            items={pickerData.festivals}
                            selectedIds={form.festival_ids}
                            onChange={set('festival_ids')}
                            placeholder="Search festivals or festival series..."
                            renderLabel={f => `${f.name}${f.is_parent ? ' (Series)' : ''}`}
                            renderChip={f => f.name}
                        />
                    </div>

                    <div style={S.editorField}>
                        <label style={S.editorLabel}>Promoter</label>
                        <EntityPicker
                            items={pickerData.promoters}
                            selectedIds={form.promoter_ids}
                            onChange={set('promoter_ids')}
                            placeholder="Search promoters..."
                            renderLabel={p => `${p.name}${p.city ? ` — ${p.city}` : ''}`}
                            renderChip={p => p.name}
                        />
                    </div>

                    <div style={S.editorField}>
                        <label style={S.editorLabel}>Venue</label>
                        <EntityPicker
                            items={pickerData.venues}
                            selectedIds={form.venue_ids}
                            onChange={set('venue_ids')}
                            placeholder="Search venues..."
                            renderLabel={v => `${v.name}${v.city ? ` — ${v.city}` : ''}`}
                            renderChip={v => v.name}
                        />
                    </div>
                </>
            )}

            <div style={S.editorField}>
                <label style={S.editorLabel}>Location</label>
                <div style={S.editorLocationRow}>
                    <select style={S.editorSelect} value={form.country_id} onChange={handleCountryChange}>
                        <option value="">All Countries</option>
                        {countries.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                    {form.country_id && (
                        <select style={S.editorSelect} value={form.state_id} onChange={e => set('state_id')(e.target.value)}>
                            <option value="">All Regions</option>
                            {editorStates.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                        </select>
                    )}
                </div>
            </div>

            {error && <div style={S.editorError}>{error}</div>}

            <div style={S.editorActions}>
                <button style={S.editorSaveBtn} onClick={onSave} disabled={saving}>
                    {saving ? 'Saving...' : 'Save Feed View'}
                </button>
                <button style={S.editorCancelBtn} onClick={onCancel} disabled={saving}>
                    Cancel
                </button>
            </div>
        </div>
    );
}

// ── Feed Page ─────────────────────────────────────────────────────────────────

const DEFAULT_VIEW = {
    id: 'default',
    name: 'Default Feed View',
    sort_by: 'posted_desc',
    event_visibility: 'all',
    country_id: null,
    state_id: null,
    artist_ids: [],
    festival_ids: [],
    promoter_ids: [],
    venue_ids: [],
};

const BLANK_FORM = {
    name: '',
    sort_by: 'posted_desc',
    event_visibility: 'all',
    country_id: '',
    state_id: '',
    artist_ids: [],
    festival_ids: [],
    promoter_ids: [],
    venue_ids: [],
};

function Feed() {
    const [items,       setItems]       = useState([]);
    const [hasMore,     setHasMore]     = useState(false);
    const [offset,      setOffset]      = useState(0);
    const [loading,     setLoading]     = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error,       setError]       = useState(null);

    // Attended/performed/worked status, keyed by event_id
    const [statusMap, setStatusMap] = useState({});

    // Current user identity, resolved once from the ID token (shared by all cards)
    const [currentUserId, setCurrentUserId] = useState(null);
    const [isAdmin, setIsAdmin] = useState(false);

    // Countries list — comes back on every /feed response, used by the editor's
    // Location dropdown (no separate fetch needed for the country list itself)
    const [countries, setCountries] = useState([]);

    // ── Feed Views ────────────────────────────────────────────────────────────
    const [feedViews,      setFeedViews]      = useState([]);
    const [selectedViewId, setSelectedViewId] = useState('default');

    const [editorOpen,   setEditorOpen]   = useState(false);
    const [editorMode,   setEditorMode]   = useState(null); // 'create' | 'edit'
    const [editorForm,   setEditorForm]   = useState(BLANK_FORM);
    const [editorSaving, setEditorSaving] = useState(false);
    const [editorError,  setEditorError]  = useState(null);
    const [editorStates, setEditorStates] = useState([]);

    // Artist/Festival/Promoter/Venue lists for the pickers — lazy-loaded the
    // first time Create New or Edit is opened, then cached for the session.
    const [pickerData,    setPickerData]    = useState(null);
    const [pickerLoading, setPickerLoading] = useState(false);

    const selectedView = selectedViewId === 'default'
        ? DEFAULT_VIEW
        : (feedViews.find(v => v.id === selectedViewId) || DEFAULT_VIEW);

    // Resolve current user + admin status from the ID token
    useEffect(() => {
        getIdToken().then(token => {
            if (token) {
                try {
                    const payload = JSON.parse(atob(token.split('.')[1]));
                    setCurrentUserId(payload.sub);
                    const groups = payload['cognito:groups'];
                    setIsAdmin(Array.isArray(groups) ? groups.includes('admins') : false);
                } catch { }
            }
        }).catch(() => { });
    }, []);

    // Load the user's saved feed views once on mount
    useEffect(() => {
        (async () => {
            try {
                const token = await getIdToken();
                const res = await axios.get(`${config.API_URL}/feed-views`, {
                    headers: { Authorization: token },
                });
                setFeedViews(res.data.views || []);
            } catch (e) {
                // Not logged in, or failed — Default Feed View still works fine
            }
        })();
    }, []);

    // ── Fetch feed ────────────────────────────────────────────────────────────

    const fetchFeed = useCallback(async ({ reset = false } = {}) => {
        try {
            const session = await fetchAuthSession();
            const token = session.tokens?.idToken?.toString();
            const params = {
                offset: reset ? 0 : offset,
                sort_by: selectedView.sort_by,
                event_visibility: selectedView.event_visibility || 'all',
            };
            if (selectedView.country_id) params.country_id = selectedView.country_id;
            if (selectedView.state_id)   params.state_id   = selectedView.state_id;
            if (selectedView.artist_ids?.length)   params.artist_ids   = selectedView.artist_ids.join(',');
            if (selectedView.festival_ids?.length) params.festival_ids = selectedView.festival_ids.join(',');
            if (selectedView.promoter_ids?.length) params.promoter_ids = selectedView.promoter_ids.join(',');
            if (selectedView.venue_ids?.length)    params.venue_ids    = selectedView.venue_ids.join(',');

            const res = await axios.get(`${config.API_URL}/feed`, {
                params,
                headers: { Authorization: `Bearer ${token}` },
            });

            const { items: newItems, has_more, offset: newOffset, filters } = res.data;

            if (reset) {
                setItems(newItems);
            } else {
                setItems(prev => [...prev, ...newItems]);
            }
            setHasMore(has_more);
            setOffset(newOffset);

            if (filters?.countries) setCountries(filters.countries);

            // Batch-fetch attended/performed/worked status for the newly loaded events
            const ids = newItems.map(i => i.event_id);
            if (ids.length > 0) {
                try {
                    const batch = await fetchBatchEventStatus(ids);
                    setStatusMap(prev => ({ ...prev, ...batch }));
                } catch (e) {
                    // Not logged in, or status fetch failed — cards just show default state
                }
            }

        } catch (err) {
            setError('Failed to load feed.');
            console.error(err);
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [offset, selectedViewId, feedViews]);

    // Re-fetch whenever the selected view (or its saved definition) changes
    useEffect(() => {
        document.title = 'SHARC - Event Feed';
        setLoading(true);
        setItems([]);
        setOffset(0);
        fetchFeed({ reset: true });
        return () => { document.title = 'SHARC - Shows Archive'; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedViewId, feedViews]);

    const handleLoadMore = () => {
        setLoadingMore(true);
        fetchFeed({ reset: false });
    };

    const handleAttendedChange = (eventId, result) => {
        setStatusMap(prev => ({
            ...prev,
            [eventId]: {
                attended: result.attended,
                performed: result.performed || false,
                worked: result.worked || false,
            },
        }));
    };

    // ── Feed View handlers ───────────────────────────────────────────────────

    const ensurePickerData = useCallback(async () => {
        if (pickerData || pickerLoading) return;
        setPickerLoading(true);
        try {
            const [artistsRes, festivalsRes, promotersRes, venuesRes] = await Promise.all([
                axios.get(`${config.API_URL}/artists`),
                axios.get(`${config.API_URL}/festivals`),
                axios.get(`${config.API_URL}/promoters`),
                axios.get(`${config.API_URL}/venues`),
            ]);
            setPickerData({
                artists: artistsRes.data || [],
                festivals: festivalsRes.data.festivals || [],
                promoters: promotersRes.data.promoters || [],
                venues: venuesRes.data || [],
            });
        } catch (e) {
            // Leave pickerData null — editor shows the loading state indefinitely,
            // which is an acceptable degraded state for a rare failure here.
        } finally {
            setPickerLoading(false);
        }
    }, [pickerData, pickerLoading]);

    const handleSelectView = (value) => {
        setSelectedViewId(value === 'default' ? 'default' : Number(value));
        setEditorOpen(false);
    };

    const handleCreateNew = () => {
        setEditorMode('create');
        setEditorForm({ ...BLANK_FORM });
        setEditorError(null);
        setEditorOpen(true);
        ensurePickerData();
    };

    const handleEditView = () => {
        if (selectedViewId === 'default') return;
        const view = feedViews.find(v => v.id === selectedViewId);
        if (!view) return;
        setEditorMode('edit');
        setEditorForm({
            name: view.name,
            sort_by: view.sort_by,
            event_visibility: view.event_visibility || 'all',
            country_id: view.country_id || '',
            state_id: view.state_id || '',
            artist_ids: view.artist_ids || [],
            festival_ids: view.festival_ids || [],
            promoter_ids: view.promoter_ids || [],
            venue_ids: view.venue_ids || [],
        });
        setEditorError(null);
        setEditorOpen(true);
        ensurePickerData();
    };

    const handleDeleteView = async () => {
        if (selectedViewId === 'default') return;
        const view = feedViews.find(v => v.id === selectedViewId);
        if (!view) return;
        if (!window.confirm(`Delete the feed view "${view.name}"? This cannot be undone.`)) return;
        try {
            const token = await getIdToken();
            await axios.delete(`${config.API_URL}/feed-views/${selectedViewId}`, {
                headers: { Authorization: token },
            });
            setFeedViews(prev => prev.filter(v => v.id !== selectedViewId));
            setSelectedViewId('default');
            setEditorOpen(false);
        } catch (e) {
            alert('Failed to delete feed view. Please try again.');
        }
    };

    const handleCancelEditor = () => {
        setEditorOpen(false);
        setEditorError(null);
    };

    const handleSaveEditor = async () => {
        if (!editorForm.name.trim()) {
            setEditorError('Please enter a name for this feed view.');
            return;
        }
        setEditorSaving(true);
        setEditorError(null);
        try {
            const token = await getIdToken();
            const payload = {
                name: editorForm.name.trim(),
                sort_by: editorForm.sort_by,
                event_visibility: editorForm.event_visibility || 'all',
                country_id: editorForm.country_id || null,
                state_id: editorForm.state_id || null,
                artist_ids: editorForm.artist_ids,
                festival_ids: editorForm.festival_ids,
                promoter_ids: editorForm.promoter_ids,
                venue_ids: editorForm.venue_ids,
            };

            if (editorMode === 'create') {
                const res = await axios.post(`${config.API_URL}/feed-views`, payload, {
                    headers: { Authorization: token },
                });
                setFeedViews(prev => [...prev, res.data]);
                setSelectedViewId(res.data.id);
            } else {
                const res = await axios.put(`${config.API_URL}/feed-views/${selectedViewId}`, payload, {
                    headers: { Authorization: token },
                });
                setFeedViews(prev => prev.map(v => v.id === selectedViewId ? res.data : v));
            }
            setEditorOpen(false);
        } catch (e) {
            setEditorError('Failed to save feed view. Please try again.');
        } finally {
            setEditorSaving(false);
        }
    };

    // Fetch the editor's Region options whenever its selected Country changes.
    // Reuses the /feed endpoint's existing country-scoped states list rather
    // than adding a new endpoint — the feed items in the response are simply
    // discarded, only filters.states is used.
    useEffect(() => {
        if (!editorOpen) return;
        if (!editorForm.country_id) {
            setEditorStates([]);
            return;
        }
        (async () => {
            try {
                const token = await getIdToken();
                const res = await axios.get(`${config.API_URL}/feed`, {
                    params: { offset: 0, sort_by: 'posted_desc', country_id: editorForm.country_id },
                    headers: { Authorization: `Bearer ${token}` },
                });
                setEditorStates(res.data.filters?.states || []);
            } catch (e) {
                setEditorStates([]);
            }
        })();
    }, [editorForm.country_id, editorOpen]);

    // ── Render ────────────────────────────────────────────────────────────────

    return (
        <div style={S.page}>
            <div style={S.container}>

                {/* Page header + Feed View controls */}
                <div style={S.headerRow}>
                    <h1 style={S.heading}>Event Feed</h1>
                    <div style={S.viewControls}>
                        <select
                            style={S.viewSelect}
                            value={selectedViewId}
                            onChange={e => handleSelectView(e.target.value)}
                        >
                            <option value="default">Default Feed View</option>
                            {feedViews.map(v => (
                                <option key={v.id} value={v.id}>{v.name}</option>
                            ))}
                        </select>
                        <button style={S.viewLinkBtn} onClick={handleCreateNew}>Create New</button>
                        {selectedViewId !== 'default' && (
                            <>
                                <button style={S.viewLinkBtn} onClick={handleEditView}>Edit</button>
                                <button style={S.viewLinkBtn} onClick={handleDeleteView}>Delete</button>
                            </>
                        )}
                    </div>
                </div>

                {editorOpen && (
                    <FeedViewEditor
                        mode={editorMode}
                        form={editorForm}
                        setForm={setEditorForm}
                        onSave={handleSaveEditor}
                        onCancel={handleCancelEditor}
                        saving={editorSaving}
                        error={editorError}
                        pickerData={pickerData}
                        pickerLoading={pickerLoading}
                        countries={countries}
                        editorStates={editorStates}
                    />
                )}

                {/* Feed content */}
                {loading && (
                    <div style={S.message}>Loading feed...</div>
                )}

                {error && !loading && (
                    <div style={S.message}>No Results Found</div>
                )}

                {!loading && !error && items.length === 0 && (
                    <div style={S.message}>No Results Found</div>
                )}

                {!loading && items.map((item, idx) => (
                    <FeedCard
                        key={`${item.feed_type}-${item.event_id}-${item.ts}-${idx}`}
                        item={item}
                        status={statusMap[item.event_id]}
                        onAttendedChange={handleAttendedChange}
                        currentUserId={currentUserId}
                        isAdmin={isAdmin}
                    />
                ))}

                {/* Load more */}
                {hasMore && !loading && (
                    <div style={S.loadMoreWrap}>
                        <button
                            style={S.loadMoreBtn}
                            onClick={handleLoadMore}
                            disabled={loadingMore}
                        >
                            {loadingMore ? 'Loading...' : 'Load More'}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}

export default Feed;
const S = {
    page: {
        backgroundColor: 'transparent', // background photo shows through (index.css)
        minHeight: '100vh',
        padding: '32px 20px 60px',
        color: '#e0e0e0',
    },
    container: {
        maxWidth: '860px',
        margin: '0 auto',
    },
    heading: {
        fontSize: '26px',
        fontWeight: 'bold',
        color: '#e0e0e0',
        marginTop: 0,
        marginBottom: 0,
        whiteSpace: 'nowrap',
    },

    // ── Header row + Feed View controls ──
    headerRow: {
        display: 'flex',
        flexWrap: 'nowrap',
        alignItems: 'center',
        gap: '16px',
        marginBottom: '24px',
    },
    viewControls: {
        display: 'flex',
        flexWrap: 'nowrap',
        alignItems: 'center',
        gap: '14px',
    },
    viewSelect: {
        backgroundColor: '#2c2c2c',
        border: '1px solid #444',
        borderRadius: '4px',
        color: '#e0e0e0',
        fontSize: '14px',
        padding: '7px 12px',
        cursor: 'pointer',
        outline: 'none',
        minWidth: '200px',
        flexShrink: 0,
    },
    viewLinkBtn: {
        background: 'transparent',
        border: 'none',
        color: '#bb86fc',
        fontSize: '14px',
        fontWeight: '600',
        cursor: 'pointer',
        padding: 0,
        whiteSpace: 'nowrap',
        flexShrink: 0,
    },

    // ── Feed View editor panel ──
    editorPanel: {
        backgroundColor: '#1a1a1a',
        border: '1px solid #333',
        borderRadius: '8px',
        padding: '20px 22px',
        marginBottom: '28px',
    },
    editorHeader: {
        fontSize: '17px',
        fontWeight: 'bold',
        color: '#e0e0e0',
        marginBottom: '16px',
    },
    editorField: {
        marginBottom: '16px',
    },
    editorLabel: {
        display: 'block',
        fontSize: '11px',
        color: '#888',
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
        marginBottom: '6px',
    },
    editorInput: {
        backgroundColor: '#2c2c2c',
        border: '1px solid #444',
        borderRadius: '4px',
        color: '#e0e0e0',
        fontSize: '14px',
        padding: '9px 12px',
        width: '100%',
        boxSizing: 'border-box',
        outline: 'none',
    },
    editorSelect: {
        backgroundColor: '#2c2c2c',
        border: '1px solid #444',
        borderRadius: '4px',
        color: '#e0e0e0',
        fontSize: '14px',
        padding: '9px 12px',
        cursor: 'pointer',
        outline: 'none',
        minWidth: '200px',
    },
    editorLocationRow: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '12px',
    },
    editorLoading: {
        color: '#888',
        fontSize: '13px',
        padding: '8px 0',
        marginBottom: '8px',
    },
    editorError: {
        color: '#cf6679',
        fontSize: '13px',
        marginBottom: '12px',
    },
    editorActions: {
        display: 'flex',
        gap: '10px',
        marginTop: '20px',
        paddingTop: '16px',
        borderTop: '1px solid #2e2e2e',
    },
    editorSaveBtn: {
        backgroundColor: '#bb86fc',
        color: '#121212',
        border: 'none',
        borderRadius: '4px',
        padding: '9px 22px',
        fontSize: '14px',
        fontWeight: 'bold',
        cursor: 'pointer',
    },
    editorCancelBtn: {
        backgroundColor: 'transparent',
        color: '#aaa',
        border: '1px solid #444',
        borderRadius: '4px',
        padding: '9px 18px',
        fontSize: '14px',
        cursor: 'pointer',
    },

    // ── Entity picker (Artist/Festival/Promoter/Venue multi-select) ──
    pickerChips: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '6px',
        marginBottom: '8px',
    },
    pickerChip: {
        backgroundColor: '#2c2c2c',
        border: '1px solid #bb86fc',
        borderRadius: '4px',
        padding: '4px 10px',
        fontSize: '13px',
        color: '#bb86fc',
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
    },
    pickerChipRemove: {
        cursor: 'pointer',
        color: '#888',
    },
    pickerInput: {
        backgroundColor: '#2c2c2c',
        border: '1px solid #444',
        padding: '9px 12px',
        fontSize: '14px',
        color: '#e0e0e0',
        width: '100%',
        boxSizing: 'border-box',
        outline: 'none',
    },
    pickerDropdown: {
        position: 'absolute',
        top: '100%',
        left: 0,
        right: 0,
        backgroundColor: '#2c2c2c',
        border: '1px solid #444',
        borderTop: 'none',
        borderRadius: '0 0 4px 4px',
        maxHeight: '220px',
        overflowY: 'auto',
        zIndex: 100,
    },
    pickerNoResults: {
        padding: '9px 14px',
        fontSize: '14px',
        color: '#666',
    },
    pickerOption: {
        padding: '9px 14px',
        fontSize: '14px',
        cursor: 'pointer',
        borderBottom: '1px solid #333',
    },

    // ── Card ──
    card: {
        backgroundColor: '#1e1e1e',
        border: '1px solid #2e2e2e',
        borderRadius: '8px',
        marginBottom: '20px',
        padding: '20px 22px',
    },
    cardInner: {
        display: 'flex',
        gap: '20px',
        alignItems: 'flex-start',
    },
    cardText: {
        flex: 1,
        minWidth: 0,
    },
    imageWrap: {
        flexShrink: 0,
        width: '160px',
    },
    imageBox: {
        position: 'relative',
        width: '160px',
        paddingBottom: '160px', // 1:1 square
        backgroundColor: '#161616',
        borderRadius: '6px',
        border: '1px solid #333',
        overflow: 'hidden',
    },
    cardImage: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        objectFit: 'contain',
        display: 'block',
    },

    // ── Card header ──
    cardHeader: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'baseline',
        gap: '4px',
        marginBottom: '10px',
        fontSize: '15px',
    },
    feedLabel: {
        color: '#bb86fc',
        fontWeight: 'bold',
    },
    headerSep: {
        color: '#aaa',
    },
    postedBy: {
        color: '#bb86fc',
        fontWeight: '600',
    },
    postedAt: {
        color: '#777',
        fontSize: '13px',
        marginLeft: '8px',
    },

    // ── Card body ──
    eventDate: {
        fontSize: '15px',
        fontWeight: '600',
        color: '#e0e0e0',
        marginBottom: '4px',
    },
    metaLine: {
        fontSize: '14px',
        color: '#ccc',
        marginBottom: '3px',
    },
    venueName: {
        color: '#e0e0e0',
    },
    venueLink: {
        color: '#bb86fc',
        fontWeight: 'bold',
        textDecoration: 'none',
    },
    artistLink: {
        color: '#ffffff',
        fontWeight: 'bold',
        textDecoration: 'none',
    },
    lineupSep: {
        color: '#e0e0e0',
        fontWeight: 'normal',
    },
    location: {
        color: '#aaa',
    },
    eventTitle: {
        fontSize: '14px',
        marginBottom: '3px',
    },
    lineup: {
        fontSize: '15px',
        color: '#e0e0e0',
        marginTop: '10px',
        lineHeight: '1.5',
    },
    purpleLink: {
        color: '#bb86fc',
        textDecoration: 'none',
    },
    viewLink: {
        color: '#bb86fc',
        textDecoration: 'none',
        fontSize: '13px',
    },

    // ── Action row (full card width, sits below the text/image row) ──
    actionRow: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '16px',
        marginTop: '14px',
        paddingTop: '12px',
        borderTop: '1px solid #2e2e2e',
        alignItems: 'center',
    },
    actionLinkBtn: {
        background: 'transparent',
        border: 'none',
        color: '#bb86fc',
        fontSize: '14px',
        fontWeight: '600',
        cursor: 'pointer',
        padding: 0,
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
    },
    chevron: {
        fontSize: '9px',
        opacity: 0.75,
    },
    copiedToast: {
        color: '#66bb6a',
        fontSize: '13px',
        fontWeight: '600',
    },

    // ── Attended / Performed / Worked (matches EventDetail exactly) ──
    attendedBtnOff: { backgroundColor: 'transparent', color: '#aaa', border: '1px solid #555', borderRadius: '6px', padding: '5px 14px', fontSize: '13px', cursor: 'pointer', fontWeight: '500', whiteSpace: 'nowrap' },
    attendedBtnOn: { backgroundColor: '#1a3a1a', color: '#66bb6a', border: '1px solid #66bb6a', borderRadius: '6px', padding: '5px 14px', fontSize: '13px', cursor: 'pointer', fontWeight: 'bold', whiteSpace: 'nowrap' },
    performedBadge: { backgroundColor: '#1a2a3a', color: '#64b5f6', border: '1px solid #64b5f6', borderRadius: '6px', padding: '5px 14px', fontSize: '13px', fontWeight: 'bold', whiteSpace: 'nowrap' },
    workedBadge: { backgroundColor: '#2b1a0d', color: '#ff9800', border: '1px solid #7a4200', borderRadius: '6px', padding: '5px 14px', fontSize: '13px', fontWeight: 'bold', whiteSpace: 'nowrap' },

    // ── Inline expand panel (Discussion / Footnotes) — full card width ──
    inlinePanel: {
        marginTop: '14px',
        paddingTop: '14px',
        borderTop: '1px solid #2e2e2e',
    },
    inlineLoading: { color: '#888', fontSize: '13px', padding: '8px 0' },
    inlineError: { color: '#cf6679', fontSize: '13px', padding: '4px 0' },
    inlineEmpty: { color: '#555', fontSize: '13px', padding: '8px 0', fontStyle: 'italic' },
    inlineSignInPrompt: { backgroundColor: '#181818', border: '1px solid #2a2a2a', borderRadius: '6px', padding: '14px', textAlign: 'center', color: '#666', fontSize: '13px' },

    inlinePostsList: { display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '12px' },
    inlinePostItem: { backgroundColor: '#181818', border: '1px solid #2a2a2a', borderRadius: '6px', padding: '10px 12px' },
    inlinePostMeta: { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' },
    inlineAuthorName: { fontWeight: 'bold', color: '#bb86fc', fontSize: '13px', textDecoration: 'none' },
    inlinePostTime: { fontSize: '11px', color: '#555' },
    inlineEditedTag: { fontSize: '11px', color: '#444', fontStyle: 'italic' },
    inlinePostBody: { fontSize: '13px', color: '#ddd', lineHeight: '1.5', whiteSpace: 'pre-wrap', wordBreak: 'break-word' },
    inlinePostActions: { display: 'flex', gap: '12px', marginTop: '8px' },
    inlineActionBtn: { background: 'none', border: 'none', color: '#777', fontSize: '12px', cursor: 'pointer', padding: 0 },
    inlineReplyBtn: { background: 'none', border: 'none', color: '#777', fontSize: '12px', cursor: 'pointer', padding: 0 },
    inlineRepliesWrap: { marginTop: '8px', marginLeft: '16px', borderLeft: '2px solid #2a2a2a', paddingLeft: '12px', display: 'flex', flexDirection: 'column', gap: '8px' },
    inlineReplyItem: { backgroundColor: '#141414', borderRadius: '5px', padding: '8px 10px' },

    inlineComposer: { marginTop: '4px' },
    inlineTextarea: {
        backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '6px',
        padding: '10px', fontSize: '13px', color: '#e0e0e0', width: '100%',
        minHeight: '60px', resize: 'vertical', lineHeight: '1.5', boxSizing: 'border-box',
    },
    inlineComposerActions: { display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '6px' },
    inlinePostBtn: {
        backgroundColor: '#bb86fc', color: '#121212', border: 'none', borderRadius: '4px',
        padding: '6px 16px', fontSize: '13px', fontWeight: 'bold', cursor: 'pointer',
    },
    inlineCancelBtn: {
        backgroundColor: 'transparent', color: '#aaa', border: '1px solid #444', borderRadius: '4px',
        padding: '6px 14px', fontSize: '13px', cursor: 'pointer',
    },

    // ── Footnotes (mirrors EventDetail's read-only Footnotes tab) ──
    footnoteList: { display: 'flex', flexDirection: 'column', gap: '10px' },
    footnoteCard: { backgroundColor: '#181818', border: '1px solid #2a2a2a', borderRadius: '6px', padding: '12px 14px', display: 'flex', gap: '12px', alignItems: 'flex-start' },
    footnoteNumber: { fontSize: '12px', color: '#666', fontWeight: 'bold', minWidth: '16px', paddingTop: '2px' },
    footnoteContent: { flex: 1 },
    footnoteDescription: { fontSize: '13px', color: '#e0e0e0', marginBottom: '6px', lineHeight: '1.5' },
    footnoteMeta: { display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' },
    footnoteArtistLink: { color: '#bb86fc', textDecoration: 'none', fontSize: '12px', fontWeight: 'bold', backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '4px', padding: '2px 8px' },
    footnoteUrlLink: { color: '#bb86fc', textDecoration: 'none', fontSize: '12px', fontWeight: 'bold' },

    // ── Misc ──
    message: {
        color: '#888',
        fontSize: '15px',
        textAlign: 'center',
        padding: '60px 0',
    },
    errorMsg: {
        color: '#cf6679',
        fontSize: '15px',
        textAlign: 'center',
        padding: '40px 0',
    },
    loadMoreWrap: {
        textAlign: 'center',
        marginTop: '24px',
    },
    loadMoreBtn: {
        backgroundColor: '#2c2c2c',
        border: '1px solid #444',
        borderRadius: '4px',
        color: '#e0e0e0',
        fontSize: '14px',
        padding: '10px 28px',
        cursor: 'pointer',
    },
};
